<template>
  <v-container fluid>
    <v-dialog v-model="visibleDialog" persistent max-width="950">
      <v-card id="upload_document_card">
        <v-card-title>{{ $t('documents.content.uploadDocument') }}</v-card-title>
        <v-card-text>
          <v-text-field
            id="upload_document_filename" v-model="fileName" :disabled="bulkUploadMode && !asicMode"
            :label="$t('main.name')"
          ></v-text-field>
          <drag-drop-upload
            id="upload_file" :key="dragDropKey" given-height="20vh" max-width="auto"
            :valid-formats="['application/pdf']" :current-file="uploadedFiles" :description="$t('dragDrop.uploadFiles')"
            @uploaded="handleDragDrop"
          >
            <v-progress-circular
              v-if="isUploading" slot="progress-bar" indeterminate color="primary"
              class="mb-4"
            ></v-progress-circular>
          </drag-drop-upload>
          <v-switch
            id="ordered_switch" v-model="chosenSignatureType" inset color="blue darken-2"
            :false-value="types.UNORDERED" :true-value="types.ORDERED" :label="chosenSignatureType === types.UNORDERED
              ? $t('documentSignatureCustomization.content.createChain.chainType.unorderedDescription')
              : $t('documentSignatureCustomization.content.createChain.chainType.orderedDescription')"
            prepend-icon="mdi-graph-outline" @click="_computedWatchers.canSign.run()"
          >
          </v-switch>
          <v-switch
            v-if="signingTrailEnabled && !asicMode" id="signingtrail_switch" v-model="useSigningTrail"
            color="blue darken-2" :label="useSigningTrail
              ? $t('documentSignatureCustomization.content.createChain.useSigningTrail.true')
              : $t('documentSignatureCustomization.content.createChain.useSigningTrail.false')"
            prepend-icon="mdi-file-certificate-outline" inset
          >
          </v-switch>

          <v-row v-if="signingTrailEnabled && useSigningTrail && !asicMode" align="center">
            <v-col cols="1" />
            <v-col>
              <v-radio-group v-model="attachedOrDetachedSigningTrail" class="pt-0 mt-0" row>
                <v-radio
                  id="radio_attached" color="blue darken-2"
                  :label="$t('documentSignatureCustomization.content.createChain.useSigningTrail.signingTrailMode.ATTACHED')"
                  value="ATTACHED"
                />
                <v-radio
                  id="radio_detached" color="blue darken-2"
                  :label="$t('documentSignatureCustomization.content.createChain.useSigningTrail.signingTrailMode.DETACHED')"
                  value="DETACHED"
                />
              </v-radio-group>
            </v-col>
          </v-row>

          <v-switch
            v-if="bulkUploadMode" id="bulk_switch" v-model="asicMode" color="blue darken-2" inset :label="asicMode
              ? $t('documentSignatureCustomization.content.createChain.asicExplainer')
              : $t('documentSignatureCustomization.content.createChain.nonAsicExplainer')"
            prepend-icon="mdi-archive-arrow-down"
          ></v-switch>
          <v-data-table
            id="virtual-scroll-table" ref="dtable" :headers="headers" :items="addedUsers"
            item-key="uniqueId" :items-per-page="-1" :no-data-text="$t('dataTable.content.noDataAvailable')"
            hide-default-footer dense class="mb-2"
          >
            <template #body="props">
              <draggable :list="addedUsers" tag="tbody" @change="_computedWatchers.canSign.run()">
                <tr v-for="(user, index) in props.items" :key="index">
                  <td>
                    <span>{{ getRank(user) }}</span>
                  </td>
                  <td>
                    <long-text :text="getFullName(user)">
                    </long-text>
                  </td>
                  <td>
                    <span id="user_email" class="caption">
                      &lt;<long-text :text="user.email" :max-char="29">
                      </long-text>&gt;
                    </span>
                  </td>
                  <td>
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          id="toggle_user_role" v-bind="attrs" icon
                          :disabled="!!userGroupsEnabled && isUserDisabledForRoleChange(user)" v-on="on"
                          @click="user.role = getToggledUserRole(user)"
                        >
                          <v-icon>
                            {{ getToggledUserRoleIcon(user.role) }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ getRoleTooltipText(user.role) }}</span>
                    </v-tooltip>
                  </td>
                  <td align="right">
                    <v-row>
                      <v-spacer></v-spacer>

                      <!-- Menu for setting Subsigner -->
                      <v-btn
                        v-show="user.role === memberRole.CLERK" id="set_subsigner_button" text class="pa-0"
                        @click="showSubsignerMenu(user, index, $event['clientX'], $event['clientY']) "
                      >
                        <v-icon>mdi-account-supervisor</v-icon>
                        {{ user.subsignerName ? $t('dataTable.content.action.changeSubsigner') :
                          $t('dataTable.content.action.setSubsigner') }}
                      </v-btn>

                      <v-btn text color="error" @click="removeUser(user, index)">
                        <v-icon>mdi-account-remove</v-icon>
                        {{ $t('dataTable.content.action.removeUser') }}
                      </v-btn>

                      <v-icon>mdi-drag-vertical</v-icon>
                    </v-row>
                  </td>
                </tr>
              </draggable>
            </template>
          </v-data-table>

          <v-autocomplete
            id="autocomplete_input" :key="autocompleteKey" v-model="nextUserToAdd" :items="enabledUsers"
            item-text="email" item-value="id" return-object
            :label="$t('documentSignatureCustomization.content.createChain.recipient') + `*`"
            :placeholder="$t('autocomplete.startTyping')" prepend-inner-icon="mdi-fountain-pen-tip"
            :no-data-text="noDatatext" clearable outlined hide-details dense :search-input="search"
            @update:search-input="listEnabledUsersWatcher" @keydown.enter="addUser" @click.prevent="addUser"
          >
            <template slot="item" slot-scope="{ item }">
              <v-row align-content="center" @mouseup="addUser(item)">
                <span class="body-2 py-4">{{ item.firstName }} {{ item.lastName }} <span
                  v-if="!!item.email"
                  class="caption"
                >&lt;{{ item.email }}&gt; </span></span>
                <v-spacer></v-spacer>
              </v-row>
            </template>
            <!-- Button to add user if no results found -->
            <template v-if="!userGroupsEnabled && !enabledUsers.length && displayInviteButton" #no-data>
              <v-row>
                <v-col class="ml-3">
                  <v-label class="ml-6">{{ noDatatext }}</v-label>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn id="invite_user" class="ml-3" color="primary" @click="addUserAsInvite()">
                    {{
                      $t('documentSignatureCustomization.content.createChain.iniviteUserButton') }}
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </v-autocomplete>
          <v-btn
            id="add_me" color="transparent" tile class="pl-0 pr-1 mt-2 rounded-l-xl modalButton" :ripple="false"
            :disabled="!currentUser.roles.includes('signer')" @click="addMe()"
          >
            <v-icon large class="modalButton">
              mdi-plus-circle
            </v-icon>
            {{ $t('documentSignatureCustomization.content.createChain.addme') }}
          </v-btn>
          <v-switch
            id="setSignatureFields"
            v-model="setSignaturesMode"
            class="my-4"

            color="blue darken-2"
            prepend-icon="mdi-border-radius"
            :label="$t('documentSignatureCustomization.content.createChain.setSignatureFieldButton')"
            inset
          >
          </v-switch>
          <v-divider
            class="my-6"
          ></v-divider>
          <v-text-field
            id="comment_text_field"
            v-model="creatorComment"
            prepend-icon="mdi-comment modalButtonColor--text large"
            persistent-hint
          >
            <template #label>
              <span class="text--secondary">{{ $t('documentSignatureCustomization.comments.label') }}</span>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions class="d-flex flex-row">
          <v-btn id="cancel" outlined class="my-4" color="primary" @click="backToParent(false)">
            <v-icon>mdi-close-circle-outline</v-icon>
            {{ $t('main.cancel') }}
          </v-btn>

          <!-- SIGN -->
          <v-btn
            v-if="canSign && !setSignaturesMode" id="confirm_sign" color="success" :disabled="checkSignButtonDisabled"
            :loading="isUploading" @click="uploadDocument(true)"
          >
            <template v-if="!isApproveMode">
              <v-icon>mdi-draw-pen</v-icon>
              {{ $t('main.sign') }}
            </template>
            <template v-else>
              <v-icon>mdi-check-decagram</v-icon>
              {{ $t('main.continue') }}
            </template>
          </v-btn>
          <v-btn
            v-else id="confirm_upload" color="primary" :loading="isUploading" :disabled="checkSignButtonDisabled"
            @click="uploadDocument(false)"
          >
            <v-icon>mdi-plus-circle-outline</v-icon>
            {{ $t('main.upload') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <pdf-password-modal
      ref="pdfPasswordModal" :visible.sync="isPasswordModalVisible" :pdf-filename="pdfFilename"
      @passwordSpecified="handlePasswordSpecified" @passwordCancelled="handlePasswordCancelled"
    >
    </pdf-password-modal>

    <!-- MENU -->
    <v-dialog v-model="subsignerNameMenuVisible" max-width="378">
      <v-card id="subsigner_card" class="pa-2">
        <v-card-subtitle class="subtitle-1">
          {{ $t('dataTable.content.action.setSubsignerDescription') }}
        </v-card-subtitle>
        <v-card-text>
          <v-form @submit.prevent="setSubsigner(currentEditedUserIndex)">
            <v-text-field
              id="subsigner_input" ref="subsignerInput" v-model="subsignerName" clearable
              dense hide-details :label="$t('dataTable.content.action.subsignerName')"
              @keyup.enter="setSubsigner(currentEditedUserIndex)"
            />
            <v-btn
              id="save_subsigner" class="mt-2" color="primary"
              @click="setSubsigner(currentEditedUserIndex)"
            >
              <v-icon>mdi-check-circle-outline</v-icon>
              {{ $t('main.save') }}
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="incorrectShowed" max-width="378">
      <v-card id="incorrectShowed" class="pa-10 ">
        <v-card-text style="text-align: center">
          <v-container>
            <v-row justify="center">
              <v-icon x-large class="display-3 mb-2" color="error">mdi-block-helper</v-icon>
            </v-row>
            <v-row justify="center">
              <div class="text-h6 error--text">
                {{ $t('toastMessage.incorrectPDFPasswordBlocking') }}
              </div>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn id="OK_btn" color="primary" @click="getPasswordAgain">
            {{ $t('main.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DragDropUpload from '@/components/DragDropUpload.vue'
import LongText from '@/components/LongText.vue'
import PDFPasswordModal from '@/components/PDFPasswordModal'
import { showToastError, showToastSuccess } from '@/event-bus.js'
import documents from '@/services/api/documents'
import rssp from '@/services/api/rsspUtil.js'
import seal from '@/services/api/seal.js'
import signatureChains from '@/services/api/signatureChains'
import users from '@/services/api/users'
import debounce from 'lodash.debounce'
import Draggable from 'vuedraggable'
import SignatureChainStatus from './SignatureChainStatus.js'
import SignatureChainType from './SignatureChainType'
import memberRole from './memberRole'

import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs'

export default {
  components: {
    Draggable,
    DragDropUpload,
    LongText,
    'pdf-password-modal': PDFPasswordModal
  },
  emits: ['uploaded'],
  data() {
    return {
      autocompleteKey: 1,
      visibleDialog: true,
      uploadedFiles: [],
      fileName: '',
      userGroupsEnabled: this.$store.state.remoteConfig ? this.$store.state.remoteConfig.userGroupsEnabled : false,
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      },
      signingTrailEnabled: this.$store.state.remoteConfig.signingTrailEnabled,
      useSigningTrail: true,
      attachedOrDetachedSigningTrail: 'ATTACHED',
      displayInviteButton: false,
      invitableUserEmail: '',
      noCentralKeyWarningVisible: false,
      dragDropKey: 0,
      enabledUsers: [],
      addedUsers: [],
      types: { ...SignatureChainType }, // only needed for template
      chosenSignatureType: 'UNORDERED',
      subsignerName: '',
      headers: [
        {
          text: this.$t('dataTable.header.users.rank'),
          sortable: false,
          align: 'left',
          value: 'rank'

        },
        {
          text: this.$t('dataTable.header.users.name'),
          align: 'left',
          value: 'name'
        },
        {
          text: this.$t('dataTable.header.users.email'),
          align: 'left',
          value: 'email'
        },
        {
          text: this.$t('documentSignatureCustomization.content.createChain.role.header'),
          align: 'left',
          value: 'role'

        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          width: '33%',

          sortable: false
        }
      ],
      rules: {
        required: v => !!v || this.$t('validation.form.requiredField')
      },

      search: '',
      currentEditedUserIndex: null,
      nextUserToAdd: {},
      includesHimself: false,
      // canSign: false,
      // progress bar
      isUploading: false,
      noDatatext: this.$t('autocomplete.startTyping'),
      memberRole: memberRole,
      asicMode: false,
      enabledUsersFixed: [],
      subsignerNameMenuVisible: false,
      menuX: 0,
      menuY: 0,
      incorrectShowed: false,
      pdfFilename: null,
      pdf_password: null,
      isPasswordModalVisible: false,
      passwordModalResolve: null,
      passwordModalReject: null,
      // Comments
      creatorComment: '',

      setSignaturesMode: false
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser
    },
    checkSignButtonDisabled() {
      const d = (this.uploadedFiles.length === 0) || this.addedUsers.length === 0 || this.addedUsers.every(x => x.role === memberRole.VIEWER)
      return d
    },
    bulkUploadMode() {
      return this.uploadedFiles.length > 1
    },
    canSign() {
      const hasAddedUsers = this.addedUsers.length > 0
      const isNotBulkUpload = !this.bulkUploadMode
      const isCurrentUserFirst =
        hasAddedUsers &&
        this.addedUsers[0].id === this.$store.state.currentUser.id
      const isUnorderedWithHimself =
        this.chosenSignatureType === SignatureChainType.UNORDERED && this.includesHimself

      return hasAddedUsers && isNotBulkUpload && (isCurrentUserFirst || isUnorderedWithHimself)
    },
    isApproveMode() {
      const isCurrentUserFirstApprover =
        (this.addedUsers[0].role === memberRole.APPROVER) && this.chosenSignatureType === SignatureChainType.ORDERED

      const isUnorderedCurrentUserApprover =
        this.chosenSignatureType === SignatureChainType.UNORDERED &&
        this.includesHimself &&
        this.addedUsers.some(user => user.id === this.currentUser.id && user.role === memberRole.APPROVER)

      return isCurrentUserFirstApprover || isUnorderedCurrentUserApprover
    },

    isClerkMode() {
      const isCurrentUserFirstClerk =
        (this.addedUsers[0].role === memberRole.CLERK) && this.chosenSignatureType === SignatureChainType.ORDERED

      const isUnorderedCurrentUserClerk =
        this.chosenSignatureType === SignatureChainType.UNORDERED &&
        this.includesHimself &&
        this.addedUsers.some(user => user.id === this.currentUser.id && user.role === memberRole.CLERK)

      return isCurrentUserFirstClerk || isUnorderedCurrentUserClerk
    },

    sealEnabled() {
      return this.sealStatus.enabled
    },
    sealActivated() {
      return this.sealStatus.enabled && this.sealStatus.activated
    },
    clerkEnabled() {
      // return true
      return this.$store.state.remoteConfig.clerkEnabled
    }
  },

  async mounted() {
    this.dragDropKey -= 1
    await this.listEnabledUsers()

    const pageReq = {
      maxResults: 100
    }
    const resp = await users.listEnabledUsers('', pageReq)
    this.enabledUsersFixed = resp.items

    this.sealStatus = await seal.getSealStatus()

    await this.checkCredentials()
  },
  methods: {
    handleDragDrop(e) {
      if (e === null) e = []
      this.uploadedFiles = e
    },
    async getPdfInfo(pdfDocument) {
      const metadata = await pdfDocument.getMetadata()
      const hasDigitalSignature = metadata.info.IsSignaturesPresent ? metadata.info.IsSignaturesPresent : false

      const isEncrypted = metadata.info.EncryptFilterName !== null
      const encryptionType = metadata.info.EncryptFilterName

      return {
        isEncrypted,
        encryptionType,
        hasDigitalSignature
      }
    },
    base64ToUint8Array(base64) {
      var raw = window.atob(base64)
      var uint8Array = new Uint8Array(raw.length)
      for (var i = 0; i < raw.length; i++) {
        uint8Array[i] = raw.charCodeAt(i)
      }
      return uint8Array
    },
    async uploadDocument(sign = false) {
      if (this.setSignaturesMode && !(this.addedUsers.every(usr => usr.role === memberRole.APPROVER))) {
        await this.navToSignatueField()
        return
      }

      try {
        this.isUploading = true
        if (!this.fileName) { this.fileName = this.uploadedFiles[0].name }

        const arrayBufferToBase64 = (arrayBuffer) => {
          let binary = ''
          const bytes = new Uint8Array(arrayBuffer)
          for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i])
          }
          return btoa(binary)
        }

        const readFileAsArrayBuffer = (file) => {
          return new Promise((resolve, reject) => {
            const fileReader = new FileReader()
            fileReader.onload = (e) => resolve(e.target.result)
            fileReader.onerror = (e) => reject(e)
            fileReader.readAsArrayBuffer(file)
          })
        }

        const processFiles = async () => {
          for (const file of this.uploadedFiles) {
            try {
              const arrayBuffer = await readFileAsArrayBuffer(file)

              const pdfData = arrayBufferToBase64(arrayBuffer)
              let pdfDocument

              const attachedSigningTrailMode = this.signingTrailEnabled && this.useSigningTrail && !this.asicMode && this.attachedOrDetachedSigningTrail === 'ATTACHED'

              try {
                pdfDocument = await pdfjs.getDocument({ data: atob(pdfData) }).promise

                const { isEncrypted, encryptionType, hasDigitalSignature } = await this.getPdfInfo(pdfDocument)
                if (hasDigitalSignature && attachedSigningTrailMode) {
                  showToastError(this.$t('toastMessage.cantSignAttachedSigningTrailWhenPDFAlreadySigned', { filename: file.name }))
                  // showToastError(this.$t('toastMessage.cantSignAttachedSigningTrailWhenPDFAlreadySigned'))
                  return false
                }
              } catch (e) {
                if (e.name === 'PasswordException') {
                  const passwordSpecified = await this.getPDFPassword(file.name)

                  if (passwordSpecified === null) {
                    console.error('Password dialog cancelled for file:', file.name, e)
                    this.isUploading = false
                    return false
                  }
                  const pdfPasswordModalComponent = this.$refs.pdfPasswordModal
                  pdfPasswordModalComponent.resetPassword()
                  try {
                    pdfDocument = await pdfjs.getDocument({
                      data: atob(pdfData),
                      password: passwordSpecified
                    }).promise

                    const { isEncrypted, encryptionType, hasDigitalSignature } = await this.getPdfInfo(pdfDocument)
                    if (hasDigitalSignature && attachedSigningTrailMode) {
                      showToastError(this.$t('toastMessage.cantSignAttachedSigningTrailWhenPDFAlreadySigned', { filename: file.name }))
                      return false
                    }
                  } catch (e) {
                    if (e.name === 'PasswordException') {
                      showToastError(this.$t('toastMessage.incorrectPDFPasswordBlocking'))
                      // this.incorrectShowed = true
                      // showToastError()
                      return false
                    }
                  }
                } else {
                  throw e // Re-throw other errors
                }
              }
            } catch (error) {
              console.error('Error processing file:', file.name, error)
              return false
            }
          }
          return true
        }

        // Call the function to start processing all uploaded files
        const filesAreOK = await processFiles()
        if (!filesAreOK) {
          this.isUploading = false
          return
        }

        let uploadResp

        try {
          if (this.bulkUploadMode) {
            uploadResp = await documents.uploadBatch(this.asicMode ? this.fileName : undefined, this.uploadedFiles)
          } else {
            uploadResp = await documents.uploadDocument(this.fileName, this.uploadedFiles[0])
          }
        } catch (error) {
          const errorMsg = this.getErrorMessage(error)
          showToastError(errorMsg)
        }
        if (uploadResp === undefined) {
          return
        }

        const viewerEmails = this.addedUsers.filter(user => user.role === memberRole.VIEWER || user.role === memberRole.CREATOR).map(x => x.email)

        const createSignatureChainRequest = {
          type: this.chosenSignatureType,
          signers: [],
          viewerEmails: viewerEmails,
          comment: this.creatorComment.length > 0 ? this.creatorComment : null
        }

        this.addedUsers.forEach(user => {
          if (user.role === memberRole.VIEWER) {
            return
          }
          const addableSignature = {
            email: user.email,
            clerkMode: this.clerkEnabled && (user.role === memberRole.CLERK),
            approver: user.role === memberRole.APPROVER
          }
          if (this.clerkEnabled && (user.role === memberRole.CLERK)) {
            addableSignature.subsignerName = user.subsignerName
          }

          createSignatureChainRequest.signers.push(addableSignature)
        })

        if (this.bulkUploadMode) {
          createSignatureChainRequest.batchId = uploadResp.id
        } else {
          createSignatureChainRequest.origDocId = uploadResp.id
        }

        if (this.signingTrailEnabled && !this.asicMode) {
          createSignatureChainRequest.signingTrailMode = this.useSigningTrail ? this.attachedOrDetachedSigningTrail : 'NONE'
        }

        let isBadRequest = false
        try {
          let resp
          if (this.asicMode || !this.bulkUploadMode) {
            // single chain or asic
            resp = await signatureChains.createSignatureChain(createSignatureChainRequest)
          } else {
            // parallel chains
            resp = await signatureChains.createMultiSignatureChain(createSignatureChainRequest)
          }

          if (sign && !this.bulkUploadMode) {
            if (this.canSign && this.isClerkMode) {
              this.$store.commit('setCurrentChain', resp)
              this.$router.push('/document/' + resp.origDocument.id + '/clerk/' + this.getUserSignatureIdForClerk(resp) + '/' + this.getCurrentSignatureId(resp))

              return
            }
            if (this.canSign && this.isApproveMode) {
              this.$router.push('/document/' + resp.origDocument.id + '/approve-customization/' + this.getUserSignatureIdForApprover(resp) + '/' + this.getCurrentSignatureId(resp))

              return
            }
            this.$router.push('/document/' + resp.id + '/' + resp.origDocument.id + '/customization/signature/' + this.getUserSignatureId(resp) + this.getCurrentSignatureId(resp))
          } else {
            showToastSuccess(this.$t('toastMessage.succesfullyCreated'))
          }
        } catch (error) {
          isBadRequest = error.response?.status === 400
          const errorMsg = this.getErrorMessage(error)
          showToastError(errorMsg)
        } finally {
          this.isUploading = false
        }

        if (!isBadRequest && !sign) {
          this.$store.commit('removeCurrentFile')
          this.$emit('uploaded')
          this.backToParent()
        }
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },
    listEnabledUsersWatcher: debounce(function(search) {
      this.listEnabledUsers(search)
    }, 500),
    isValidEmail(email) {
      // Regular expression for validating an email address
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    async listEnabledUsers(searchUser) {
      const pageReq = {
        maxResults: 5
      }
      let searchUserlen = 0
      try {
        searchUserlen = searchUser.length
      } catch (err) {

      }
      if (searchUserlen > 0) {
        try {
          const resp = await users.listEnabledUsers(searchUser, pageReq)
          this.enabledUsers = resp.items

          if (resp.items.length === 0) {
            this.noDatatext = this.$t('documentSignatureCustomization.content.createChain.userNotFound')

            if (this.isValidEmail(searchUser)) {
              this.invitableUserEmail = searchUser
              this.displayInviteButton = true
            } else {
              this.displayInviteButton = false
            }
          } else {
            this.displayInviteButton = false
          }
        } catch (error) {
          const errorMsg = this.getErrorMessage(error)
          showToastError(errorMsg)
        }
      } else {
        this.noDatatext = this.$t('autocomplete.startTyping')
      }
    },
    removeUser(user, index) {
      if (user.id === this.currentUser.id) {
        this.includesHimself = false
      }
      this.addedUsers.splice(index, 1)
    },

    addUser(user) {
      if (user.id === this.currentUser.id) {
        if (this.currentUser.roles.includes('signer')) {
          user.role = memberRole.CREATOR_AND_SIGNER
        } else {
          showToastError(this.$t('documentSignatureCustomization.warning.creatorCantAddAsViewer'))
          return
        }
      } else {
        if (user.roles && user.roles.includes('signer')) {
          user.role = memberRole.SIGNER
        } else {
          user.role = memberRole.VIEWER
        }
      }

      this.nextUserToAdd = user

      // we need to copy the object and assign a unique id for it to avoud Vue duplicate key warnings
      this.nextUserToAdd = Object.assign({}, this.nextUserToAdd)
      this.nextUserToAdd.uniqueId = this.addedUsers.length + 1
      // explanation: somehow a empty array is not an array: error: .include is  not a function
      if ((this.nextUserToAdd !== undefined) && (this.nextUserToAdd.email)) {
        this.addedUsers.push(this.nextUserToAdd)
        // this.$set(this.addedUsers, this.addedUsers.length - 1, this.nextUserToAdd)
        if (this.nextUserToAdd.id === this.currentUser.id) {
          this.includesHimself = true
        }
        this.nextUserToAdd = ''

        this.autocompleteKey++ // hack: this needs to Vue clear the autocomplete field correctly

        this.search = ''
        this.displayInviteButton = false
      }
      const container = this.$refs.dtable.$el
      container.scrollTop = container.scrollHeight - 1
    },
    addMe() {
      this.addUser(this.currentUser)
    },
    setSubsigner(index) {
      /* if (this.subsignerName?.trim()) {
        this.addedUsers[index].subsignerName = this.subsignerName
        this.subsignerNameMenuVisible = false // Close the menu
      } else {
      } */
      // this.addedUsers[index].subsignerName = this.subsignerName
      this.$set(this.addedUsers[index], 'subsignerName', this.subsignerName)
      this.subsignerNameMenuVisible = false // Close the menu
    },
    addUserAsInvite() {
      const invitableUser = {}
      invitableUser.roles = []
      invitableUser.roles.push('signer')
      invitableUser.email = this.invitableUserEmail
      this.addUser(invitableUser)
    },
    backToParent(e) {
      if (!e) {
        this.$store.commit('removeCurrentFile')
        this.$router.push({ name: 'SignatureChains' })
      }
    },
    getUserSignatureId(item) {
      const userSignature = item.signatures.find(signature => (signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED))
      return userSignature.id
    },
    getUserSignatureIdForApprover(item) {
      const userSignature = item.signatures.find(signature => (signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED && signature.approver === true))
      return userSignature.id
    },
    getUserSignatureIdForClerk(item) {
      const userSignature = item.signatures.find(signature => (signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED && signature.clerkMode === true))
      return userSignature.id
    },
    getCurrentSignatureId(item) {
      let currentSignatureId = '/'
      if (item.type === SignatureChainType.ORDERED) {
        currentSignatureId = ''
      } else {
        if (item.currentSignatureId === undefined) {
          currentSignatureId = ''
        } else {
          currentSignatureId += item.currentSignatureId
        }
        // currentSignatureId += item.signatures.findIndex(signature => signature.user.id === this.currentUser.id)
      }

      return currentSignatureId
    },
    getToggledUserRole(user) {
      const { role } = user

      const userHaveRoles = user.roles
      const userHaveApprover = userHaveRoles && user.roles.includes('approver')
      const userHaveSign = userHaveRoles && user.roles.includes('signer')
      const canAddApprover = userHaveApprover
      const canAddSigner = !userHaveRoles || userHaveSign
      const canAddViewer = userHaveRoles
      const canAddClerk = this.clerkEnabled && (!userHaveRoles || userHaveSign)
      if (user.id === this.currentUser.id) {
        /* switch (role) {
          case memberRole.CREATOR_AND_SIGNER:
            return this.clerkEnabled ? memberRole.CLERK : memberRole.APPROVER
          case memberRole.APPROVER:
            return this.clerkEnabled ? memberRole.CLERK : memberRole.CREATOR_AND_SIGNER
          case memberRole.CLERK:
            return memberRole.CREATOR_AND_SIGNER
          default:
            return role
        } */
        switch (role) {
          case memberRole.CREATOR_AND_SIGNER:
            return this.clerkEnabled ? memberRole.CLERK : (canAddApprover ? memberRole.APPROVER : memberRole.VIEWER)
          case memberRole.CLERK:
            return this.clerkEnabled ? (canAddApprover ? memberRole.APPROVER : memberRole.VIEWER) : memberRole.VIEWER
          case memberRole.VIEWER:
            return canAddApprover ? memberRole.APPROVER : memberRole.CREATOR_AND_SIGNER
          case memberRole.APPROVER:
            return memberRole.CREATOR_AND_SIGNER
          default:
            return role
        }
      } else {
        const rolesOrder = [
          this.memberRole.SIGNER,
          this.memberRole.CLERK,
          this.memberRole.VIEWER,
          this.memberRole.APPROVER
        ]
        const canAddMap = {
          [this.memberRole.SIGNER]: canAddSigner,
          [this.memberRole.CLERK]: canAddClerk,
          [this.memberRole.VIEWER]: canAddViewer,
          [this.memberRole.APPROVER]: canAddApprover
        }

        const currentIndex = rolesOrder.indexOf(role)
        const totalRoles = rolesOrder.length

        for (let i = 1; i <= totalRoles; i++) {
          const nextIndex = (currentIndex + i) % totalRoles
          const nextRole = rolesOrder[nextIndex]

          if (canAddMap[nextRole]) {
            return nextRole
          }
        }
        console.error('Unknown role for non-current user', role)
        return role
      }
    },
    getToggledUserRoleIcon(role) {
      switch (role) {
        case memberRole.CREATOR:
          return 'mdi-file-sign'
        case memberRole.SIGNER:
          return 'mdi-file-sign'
        case memberRole.CREATOR_AND_SIGNER:
          return 'mdi-file-sign'
        case memberRole.VIEWER:
          return 'mdi-eye-outline'
        case memberRole.CLERK:
          return 'mdi-account-tie'
        case memberRole.APPROVER:
          return 'mdi-check-decagram'
        default:
          console.error('Unknown role', role)
      }
    },
    getRoleTooltipText(role) {
      switch (role) {
        case memberRole.CREATOR_AND_SIGNER:
          return this.$t('documentSignatureCustomization.content.createChain.role.creatorExplainer')
        case memberRole.SIGNER:
          return this.$t('documentSignatureCustomization.content.createChain.role.signerExplainer')
        case memberRole.VIEWER:
          return this.$t('documentSignatureCustomization.content.createChain.role.viewerExplainer')
        case memberRole.CLERK:
          return this.$t('documentSignatureCustomization.content.createChain.role.clerkExplainer')
        case memberRole.APPROVER:
          return this.$t('signatureChains.states.approverExplainer')
        default:
          console.error('Unknown role', role)
          break
      }
    },
    getRank(user) {
      if (this.chosenSignatureType === SignatureChainType.UNORDERED || user.role === memberRole.VIEWER) return '•'
      else {
        const signers = this.addedUsers.filter(x => x.role !== memberRole.VIEWER)
        const index = signers.findIndex(x => x.uniqueId === user.uniqueId)

        return index + 1
      }
    },
    getFullName(user) {
      if (user.firstName && user.lastName) {
        return `${user.firstName} ${user.lastName}`
      } else
      if (user.firstName) {
        return `${user.firstName}`
      } else
      if (user.lastName) {
        return `${user.lastName}`
      } else return ''
    },
    async checkCredentials() {
      try {
        const resp = await rssp.listMyActivatedCredentials()
        resp.content = resp.content.filter(elem => ((elem.keyPurpose === 'ASYMMETRIC_SIGN') && (elem.certChain.length > 0)))

        const activatedCredentials = resp.content

        if (this.sealEnabled && !this.sealActivated && (activatedCredentials && activatedCredentials.length === 0)) {
          this.noCentralKeyWarningVisible = true
        }
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    isUserDisabledForRoleChange(user) {
      const isEmailInArray = this.enabledUsersFixed.some(item => item.email === user.email)
      return !isEmailInArray || (user.roles && !user.roles.includes('signer'))
    },

    showPasswordModal() {
      this.isPasswordModalVisible = true
      return new Promise((resolve, reject) => {
        this.passwordModalResolve = resolve
        this.passwordModalReject = reject
      })
    },

    async getPDFPassword(pdfFilename) {
      try {
        this.pdfFilename = pdfFilename
        const givenPassword = await this.showPasswordModal()
        return givenPassword
      } catch (error) {
        return null
      }
    },
    handlePasswordSpecified(password) {
      this.isPasswordModalVisible = false
      if (this.passwordModalResolve) {
        this.passwordModalResolve(password)
      }
    },
    handlePasswordCancelled() {
      this.isPasswordModalVisible = false
      if (this.passwordModalReject) {
        this.passwordModalReject()
      }
    },
    async getPasswordAgain() {
      this.incorrectShowed = false
      // await this.showPasswordModal()
    },
    showSubsignerMenu(item, index, x, y) {
      if (item.subsignerName) { this.subsignerName = item.subsignerName }

      this.currentEditedUserIndex = index
      this.menuX = x
      this.menuY = y
      this.subsignerNameMenuVisible = true
    },
    async navToSignatueField() {
      const uploadResp = await documents.uploadDocument(this.fileName || this.uploadedFiles[0].name, this.uploadedFiles[0])

      const createSignatureChainRequest = {
        type: this.chosenSignatureType,
        comment: this.creatorComment,
        fileName: this.fileName || this.uploadedFiles[0].name,
        addedUsers: this.addedUsers,
        useSigningTrail: this.useSigningTrail,
        attachedOrDetachedSigningTrail: this.attachedOrDetachedSigningTrail

      }

      this.$store.commit('setCurrentChain', createSignatureChainRequest)
      this.$router.push(`/document/${uploadResp.id}/create/`)
    }
  }
}

</script>

<style scoped>
#virtual-scroll-table {
  max-height: 20vh;
  overflow-y: auto;
  overflow-x: hidden !important;
}
</style>
